import * as React from "react";
import TransactionAuditTrail from "../Transaction/transaction.audittrail";
import {
  Card,
  CardHeader,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { convertToUSD } from "../../utils/Util";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router";
import PaymentMehtodType from "../../common/components/paymentmethod.type";
import PaymentStatusColumn from "../../common/components/paymentstatus.column";
import { GetDealerPlatformFeeOption } from "../../utils/UtilTarget";
import { PaymentStatus } from "../../types/enums";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setSurchargeValue } from "../../features/dealer/dealerSlice";
import { useGetMerchantCustomFieldsQuery } from "../../services/API/customFieldApiSlice";
import { RootState } from "../../app/store";
import {
  formatNumber,
  isSurchargeEnabled,
} from "../../common/components/AppUtils";
import { LocaleHelper } from "../../helpers/LocaleHelper";

export default function DashboardRecentTransactions({
  transactions,
  isTransactionsFetching = false,
}) {
  const drawerRef = React.useRef<any>();
  const [IsPlatformFeeEnabled, setIsPlatformFeeEnabled] =
    React.useState<any>(null);
  const navigate = useNavigate();
  const [hasSurchargeEverEnabled, SetHasSurchargeEverEnabled] =
    React.useState<boolean>(false);
  const dealerId = useAppSelector((state: RootState) => state.dealer.dealerId);
  const dispatch = useAppDispatch();

  /**
   * Get the currency symbol for the current country code stored in localStorage.
   */
  const countryCode = localStorage.getItem("countryCode") ?? "US";

  /**
   * Get the currency symbol for the country code, defaulting to "US" if not found
   */
  let currencySymbol: string;
  try {
    currencySymbol = LocaleHelper.getCurrencySymbol(countryCode);
  } catch {
    currencySymbol = LocaleHelper.getCurrencySymbol("US");
  }
  const currencyHeaderSuffix = currencySymbol ? ` (${currencySymbol})` : "";

  const cardTitle = () => {
    return (
      <Grid
        container
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
      >
        <Grid item xs={8}>
          Recent Transactions{" "}
          <label style={{ fontSize: "14px" }}>(Last 10)</label>
        </Grid>
        <Grid item style={{ flexGrow: "1" }}></Grid>
        <Grid item xs={2}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            onClick={() => {
              navigate("/dashboard/transactions");
            }}
          >
            <label style={{ fontSize: "13px", cursor: "pointer" }}>
              View All{" "}
            </label>
            <ArrowForwardIcon style={{ fontSize: "20px", cursor: "pointer" }} />
          </Stack>
        </Grid>
      </Grid>
    );
  };

  React.useEffect(() => {
    setIsPlatformFeeEnabled(GetDealerPlatformFeeOption());

    const isSurchargeEverEnabled = isSurchargeEnabled();
    SetHasSurchargeEverEnabled(isSurchargeEverEnabled);
    dispatch(setSurchargeValue(isSurchargeEverEnabled));
  }, []);

  // Fetch defined custom fields for the merchant
  const merchantId = localStorage.getItem("merchantId") || "";
  const { data: definedCustomFields } = useGetMerchantCustomFieldsQuery(
    merchantId,
    {
      skip: !merchantId,
      refetchOnMountOrArgChange: true,
    },
  );

  const allRows = React.useMemo(() => {
    return transactions &&
      transactions.pagedList &&
      transactions.pagedList?.data.length > 0
      ? transactions.pagedList.data.map((item, index) => {
          const row: any = {
            id: `${index}`,
            ...item,
            surcharge: item.platformFeeTypeId === 1 ? item.platformFee : 0,
            platformFee: item.platformFeeTypeId === 0 ? item.platformFee : 0,
          };
          // Map custom field values based on defined custom fields
          if (definedCustomFields && Array.isArray(definedCustomFields)) {
            definedCustomFields.forEach((def) => {
              const match = item.customFields?.find(
                (f: any) => f.name === def.name,
              );
              row[def.name] = match?.value ?? "";
            });
          }
          return row;
        })
      : [];
  }, [transactions, definedCustomFields]);

  /**
   * Use defined custom fields for column rendering.
   * Only include fields that have at least one non-empty value across all rows.
   * Separate into start and end columns based on direction property.
   */
  const customFieldLabels = React.useMemo(() => {
    if (!definedCustomFields || !Array.isArray(definedCustomFields))
      return { start: [], end: [] };

    const filteredFields = [...definedCustomFields]
      .filter((field) => {
        // Check if at least one row has a non-empty value for this field
        return allRows.some((row: any) => {
          const value = row[field.name];
          return value !== "" && value !== null && value !== undefined;
        });
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    const start = filteredFields
      .filter((field) => field.direction === "Start")
      .map((field) => ({ name: field.name, label: field.label }));

    const end = filteredFields
      .filter((field) => field.direction === "End")
      .map((field) => ({ name: field.name, label: field.label }));

    return { start, end };
  }, [definedCustomFields, allRows]);

  /**
   * Helper to get customField value by name for a given row (now direct property).
   */
  const getCustomFieldValue = (row: any, name: string): string => {
    return row[name] ?? "";
  };

  /**
   * Maps row.status to PaymentStatus enum value if it matches any enum key.
   */
  const resolvePaymentStatus = (status: string): PaymentStatus | string => {
    if (!status) return status;

    // Check if status matches any PaymentStatus enum value
    const statusKey = Object.keys(PaymentStatus).find(
      (key) =>
        PaymentStatus[key as keyof typeof PaymentStatus] === status ||
        key === status,
    );

    return statusKey
      ? PaymentStatus[statusKey as keyof typeof PaymentStatus]
      : status;
  };

  const REFUND_STATUSES = [
    PaymentStatus.Refunded,
    PaymentStatus.RefundFailed,
    PaymentStatus.Cancelled,
    PaymentStatus.AutoReversal,
  ];

  /**
   * Renders a right-aligned monetary cell. Refunded rows show the value
   * in parentheses with red text; other rows show the plain formatted value.
   */
  const renderAmountCell = (amount: number, row: any) => {
    const isRefundedPayment =
      REFUND_STATUSES.includes(row.status) ||
      row.isRefunded ||
      row.transactionTypeId === 1; // Credit Return

    return (
      <TableCell
        align="right"
        sx={{
          whiteSpace: "nowrap",
          color: isRefundedPayment ? "red" : "inherit",
        }}
      >
        {isRefundedPayment
          ? `(${formatNumber(Math.abs(amount))})`
          : formatNumber(amount)}
      </TableCell>
    );
  };

  return (
    <Card
      style={{ marginTop: "5px", border: "1px solid #cfcfcf", height: "65vh" }}
      className="boxShadow"
    >
      <CardHeader title={cardTitle()}></CardHeader>
      <TableContainer
        sx={{ height: "55vh" }}
        style={{ overflow: "auto", scrollbarWidth: "thin" }}
      >
        <Table size={"small"} className="sticky-header">
          <TableHead>
            <TableRow>
              {/* Dynamic Custom Field Columns - Start */}
              {customFieldLabels.start.map(({ name, label }) => (
                <TableCell key={`custom-header-${name}`}>{label}</TableCell>
              ))}
              <TableCell>Order#</TableCell>
              <TableCell>Reference</TableCell>
              <TableCell>Location </TableCell>
              <TableCell>Customer </TableCell>
              <TableCell>Transaction Date</TableCell>
              <TableCell sx={{ textAlign: "right" }}>
                {`Amount${currencyHeaderSuffix}`}
              </TableCell>
              {hasSurchargeEverEnabled && (
                <TableCell sx={{ textAlign: "right" }}>
                  {`Surcharge${currencyHeaderSuffix}`}
                </TableCell>
              )}
              {IsPlatformFeeEnabled && (
                <TableCell sx={{ textAlign: "right" }}>
                  {`Platform fee${currencyHeaderSuffix}`}
                </TableCell>
              )}
              {(IsPlatformFeeEnabled || hasSurchargeEverEnabled) && (
                <TableCell sx={{ textAlign: "right" }}>
                  {`Total${currencyHeaderSuffix}`}
                </TableCell>
              )}
              <TableCell>Status</TableCell>
              <TableCell style={{ display: "none" }}>Card</TableCell>
              {/* Dynamic Custom Field Columns - End */}
              {customFieldLabels.end.map(({ name, label }) => (
                <TableCell key={`custom-header-${name}`}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isTransactionsFetching ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{ height: "40vh", border: "none" }}
                >
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : allRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9}>No record found.</TableCell>
              </TableRow>
            ) : (
              allRows.map((row: any, index: any) => {
                return (
                  <TableRow
                    onClick={() => {
                      drawerRef.current.openDrawer(row);
                    }}
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                    sx={{ cursor: "pointer" }}
                  >
                    {/* Dynamic Custom Field Values - Start */}
                    {customFieldLabels.start.map(({ name }) => (
                      <TableCell
                        key={`custom-value-${index}-${name}`}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {getCustomFieldValue(row, name)}
                      </TableCell>
                    ))}
                    <TableCell>{row.invoiceId}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {row.paymentId}
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {row.dealerLocation}
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {row.customerName}
                    </TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {row.transactionDate.toUSDateFormat("M/DD/YYYY hh:mm A")}
                    </TableCell>
                    {renderAmountCell(row.transactionAmount, row)}
                    {hasSurchargeEverEnabled &&
                      renderAmountCell(row.surcharge, row)}
                    {IsPlatformFeeEnabled &&
                      renderAmountCell(row.platformFee, row)}
                    {(IsPlatformFeeEnabled || hasSurchargeEverEnabled) &&
                      renderAmountCell(row.totalAmount, row)}
                    <PaymentStatusColumn
                      isRefunded={row.isRefunded}
                      isCancelled={row.isCancelled}
                      status={resolvePaymentStatus(row.status)}
                      refundInitiated={row.refundInitiated}
                      cancelOrRefund={row.cancelOrRefund}
                      transactionTypeId={row.transactionTypeId}
                      renderAsColumn={true}
                      handleClick={() => {}}
                    ></PaymentStatusColumn>
                    <TableCell style={{ display: "none" }}>
                      <PaymentMehtodType
                        type={row.transactionType}
                      ></PaymentMehtodType>
                    </TableCell>
                    {/* Dynamic Custom Field Values - End */}
                    {customFieldLabels.end.map(({ name }) => (
                      <TableCell
                        key={`custom-value-${index}-${name}`}
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {getCustomFieldValue(row, name)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TransactionAuditTrail ref={drawerRef}></TransactionAuditTrail>
    </Card>
  );
}
